import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildBreadcrumbSchema } from '../../utils/seo/breadcrumbs';

function CrumbSeparator() {
  return (
    <svg
      className="flex-shrink-0 h-5 w-5 text-gray-400"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function isBreadcrumbLink(item, index, items) {
  const isLast = index === items.length - 1;
  return Boolean(item.path) && !item.current && !isLast;
}

function BreadcrumbItem({ item, isCurrent = false }) {
  if (isCurrent || item.current || !item.path) {
    return (
      <span
        className="text-gray-900 break-words min-w-0"
        aria-current={isCurrent ? 'page' : undefined}
      >
        {item.name}
      </span>
    );
  }

  return (
    <Link
      to={item.path}
      className="text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
    >
      {item.name}
    </Link>
  );
}

function MobileBreadcrumb({ items }) {
  return (
    <div className="sm:hidden min-w-0">
      <div className="text-[15px] leading-snug line-clamp-2">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <React.Fragment key={`${item.name}-${index}-mobile`}>
              {index > 0 && (
                <span className="text-gray-400 select-none" aria-hidden="true">
                  {' '}›{' '}
                </span>
              )}
              {isBreadcrumbLink(item, index, items) ? (
                <Link
                  to={item.path}
                  className="text-gray-500 hover:text-gray-700 whitespace-nowrap"
                >
                  {item.name}
                </Link>
              ) : (
                <span
                  className={isLast ? 'text-gray-900' : 'text-gray-500 whitespace-nowrap'}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.name}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Breadcrumb visual com Schema.org BreadcrumbList (complementar).
 * @param {boolean} skipSchema — evita JSON-LD duplicado quando SEO já inclui BreadcrumbList
 */
export default function BreadcrumbNav({ items = [], baseUrl, className = '', skipSchema = false }) {
  const schema = buildBreadcrumbSchema(items, baseUrl);

  if (!items.length) return null;

  return (
    <>
      {!skipSchema && (
        <Helmet prioritizeSeoTags>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      )}
      <nav
        className={`min-w-0 font-montserrat font-medium ${className}`}
        aria-label="Breadcrumb"
      >
        <MobileBreadcrumb items={items} />

        <ol className="hidden sm:flex flex-wrap items-center gap-x-2 gap-y-1">
          {items.map((item, index) => (
            <li
              key={`${item.name}-${index}`}
              className="flex items-center gap-x-2 min-w-0 max-w-full"
            >
              {index > 0 && <CrumbSeparator />}
              <BreadcrumbItem item={item} isCurrent={index === items.length - 1} />
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}
